import { forwardRef } from "react";
import { translateEducation } from "@/lib/i18n";
import { MAP_PARTY_COLORS, NO_DATA_COLOR, OUTCOME_COLORS, formatRupees, partyLabel } from "@/lib/insights";

const NAVY = "#1a1640";
const TONE = {
  more: ["#ecfdf5", "#065f46"], less: ["#fff1f2", "#9f1239"], wealthier: ["#fffbeb", "#78350f"],
  poorer: ["#f0f9ff", "#0c4a6e"], same: ["#f1f5f9", "#334155"], na: ["#f1f5f9", "#64748b"],
};

function Side({ side, label, t, lang }) {
  const ta = lang === "ta";
  const font = ta ? "'Noto Sans Tamil', 'DM Sans', sans-serif" : "'DM Sans', sans-serif";
  const color = side ? MAP_PARTY_COLORS[side.party_short] || NO_DATA_COLOR : NO_DATA_COLOR;
  const row = (k, v) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 13, marginTop: 6 }}>
      <span style={{ color: "#6b6894" }}>{k}</span>
      <span style={{ fontWeight: 700, color: NAVY, textAlign: "right", whiteSpace: "nowrap" }}>{v}</span>
    </div>
  );
  return (
    <div style={{ flex: 1, minWidth: 0, background: "#f6f3ea", borderRadius: 18, padding: 16, borderTop: `5px solid ${color}`, fontFamily: font }}>
      <div style={{ fontSize: 11, letterSpacing: ta ? 0 : "0.16em", textTransform: "uppercase", color: "#8c89ad", fontWeight: 700 }}>{label}</div>
      {side ? (
        <>
          <div style={{ marginTop: 6, fontSize: 19, fontWeight: 700, color: NAVY, lineHeight: 1.2, fontFamily: ta ? font : "'Fraunces', serif" }}>{ta ? side.mla_ta : side.mla}</div>
          <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: NAVY }}>
            <span style={{ width: 10, height: 10, borderRadius: 99, background: color, display: "inline-block" }} />
            {partyLabel(side.party_short, lang)}
          </div>
          <div style={{ marginTop: 6 }}>
            {row(t("assets_label"), formatRupees(side.assets, t))}
            {row(t("education"), translateEducation(side.education, lang))}
            {row(t("cases_label"), side.cases ?? "—")}
          </div>
        </>
      ) : (
        <div style={{ marginTop: 8, fontSize: 13, color: "#6b6894" }}>{t("not_listed")}</div>
      )}
    </div>
  );
}

// Fixed-size card captured to PNG for sharing
const ShareSeatCard = forwardRef(function ShareSeatCard({ seat, t, lang, eduText, wealthText, wealthExtra, fateText, url }, ref) {
  const ta = lang === "ta";
  const font = ta ? "'Noto Sans Tamil', 'DM Sans', sans-serif" : "'DM Sans', sans-serif";
  const both = seat.y2021 && seat.y2026;
  const verdict = (text, extra, [bg, fg]) => (
    <div style={{ flex: 1, background: bg, color: fg, borderRadius: 14, padding: "10px 14px", fontSize: 14, fontWeight: 700, lineHeight: 1.3 }}>
      {text}
      {extra && <div style={{ fontSize: 12, fontWeight: 500, opacity: 0.85, marginTop: 2 }}>{extra}</div>}
    </div>
  );
  return (
    <div ref={ref} style={{ width: 540, background: "#f8f3e4", fontFamily: font, display: "flex", flexDirection: "column", overflow: "hidden" }} data-testid="share-seat-card">
      <div style={{ background: NAVY, color: "#fff", padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontFamily: "'Fraunces', serif", fontSize: 22, fontWeight: 700 }}>NeeYosi <span style={{ fontFamily: "'Noto Sans Tamil', sans-serif", fontSize: 16, opacity: 0.8 }}>நீ யோசி</span></div>
        <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#c7c2f0" }}>2021 → 2026</div>
      </div>
      <div style={{ padding: "18px 24px 0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
          <div>
            <div style={{ fontSize: 30, fontWeight: 700, color: NAVY, lineHeight: 1.1, fontFamily: ta ? font : "'Fraunces', serif" }}>{ta ? seat.name_ta : seat.name}</div>
            <div style={{ fontSize: 13, color: "#6b6894", marginTop: 4 }}>{ta ? seat.district_ta : seat.district} · AC {seat.ac_no}</div>
          </div>
          <span style={{ background: OUTCOME_COLORS[seat.outcome_key], color: seat.outcome_key === "not_compared" ? NAVY : "#fff", borderRadius: 99, padding: "6px 12px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
            {t(`outcome_${seat.outcome_key}`)}
          </span>
        </div>
        <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
          <Side side={seat.y2021} label={t("mla_2021")} t={t} lang={lang} />
          <Side side={seat.y2026} label={t("mla_2026")} t={t} lang={lang} />
        </div>
        {both && (
          <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
            {verdict(eduText, null, TONE[seat.compare.education])}
            {verdict(wealthText, wealthExtra, TONE[seat.compare.wealth])}
          </div>
        )}
        {fateText && <div style={{ marginTop: 12, fontSize: 12.5, color: "#6b6894" }}>{fateText}</div>}
        <div style={{ marginTop: 20, padding: "12px 0 16px", borderTop: "1px solid #e8e1cc" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#5b21b6", wordBreak: "break-all" }}>{url.replace(/^https?:\/\//, "")}</div>
          <div style={{ fontSize: 10.5, color: "#8c89ad", marginTop: 4, lineHeight: 1.4 }}>{t("share_card_source")}</div>
        </div>
      </div>
    </div>
  );
});

export default ShareSeatCard;
